/**
 * Which way to point, and how high: the galactic core's direction and height
 * through the dark window, worded once for the planner, Excel/CSV, .ics and
 * the PDFs (ERI-2395).
 *
 * Height is altitude, degrees above the horizon. Azimuth is a compass bearing,
 * 0 = N, 90 = E, 180 = S, 270 = W, labelled with the planner map's 8 points.
 */

/** "SE 128°" */
export function formatAzimuth(az: number): string {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const norm = ((az % 360) + 360) % 360;
  return `${dirs[Math.round(norm / 45) % 8]} ${Math.round(norm)}°`;
}

/**
 * Above this, the core is close to straight up and a compass bearing stops
 * meaning much: a hundredth of a degree of position swings it by tens of degrees.
 */
export const OVERHEAD_ALTITUDE = 80;

/** "SE 128°", or "overhead" when the core is above OVERHEAD_ALTITUDE. */
export function formatDirection(az: number, alt: number): string {
  return alt >= OVERHEAD_ALTITUDE ? 'overhead' : formatAzimuth(az);
}

/** "24°" */
export function formatAltitude(alt: number): string {
  return `${Math.round(alt)}°`;
}

export interface CoreDirectionInput {
  galacticCoreVisible: boolean;
  riseAzimuth?: number;
  setAzimuth?: number;
  peakAzimuth?: number;
  startAltitude?: number;
  endAltitude?: number;
  peakAltitude?: number;
  corePeakTime?: string;
}

const has = (n: number | undefined): n is number => typeof n === 'number' && Number.isFinite(n);

/**
 * "Starts SE 128° at 12°, peaks S 182° at 24° at 1:10 AM, ends SW 215° at 9°",
 * or null when the core isn't visible in the dark that night.
 */
export function coreDirectionLine(day: CoreDirectionInput): string | null {
  if (!day.galacticCoreVisible) return null;
  const parts: string[] = [];
  if (has(day.riseAzimuth) && has(day.startAltitude)) {
    parts.push(`starts ${formatDirection(day.riseAzimuth, day.startAltitude)} at ${formatAltitude(day.startAltitude)}`);
  }
  if (has(day.peakAzimuth) && has(day.peakAltitude)) {
    parts.push(
      `peaks ${formatDirection(day.peakAzimuth, day.peakAltitude)} at ${formatAltitude(day.peakAltitude)}` +
        (day.corePeakTime ? ` at ${day.corePeakTime}` : ''),
    );
  }
  if (has(day.setAzimuth) && has(day.endAltitude)) {
    parts.push(`ends ${formatDirection(day.setAzimuth, day.endAltitude)} at ${formatAltitude(day.endAltitude)}`);
  }
  if (parts.length === 0) return null;
  const line = parts.join(', ');
  return line[0].toUpperCase() + line.slice(1);
}

/** Excel/CSV columns, blank when the core isn't visible. */
export function coreDirectionColumns(day: CoreDirectionInput): Record<string, string> {
  const v = day.galacticCoreVisible;
  return {
    'Start Azimuth': v && has(day.riseAzimuth) && has(day.startAltitude) ? formatDirection(day.riseAzimuth, day.startAltitude) : '',
    'Start Altitude': v && has(day.startAltitude) ? formatAltitude(day.startAltitude) : '',
    'Peak Time': v && day.corePeakTime ? day.corePeakTime : '',
    'Peak Azimuth': v && has(day.peakAzimuth) && has(day.peakAltitude) ? formatDirection(day.peakAzimuth, day.peakAltitude) : '',
    'Peak Altitude': v && has(day.peakAltitude) ? formatAltitude(day.peakAltitude) : '',
    'End Azimuth': v && has(day.setAzimuth) && has(day.endAltitude) ? formatDirection(day.setAzimuth, day.endAltitude) : '',
    'End Altitude': v && has(day.endAltitude) ? formatAltitude(day.endAltitude) : '',
  };
}

/**
 * Dark core time (ERI-262): how long the core is up while the sky is fully dark
 * and the moon is below the horizon. "3h 20m", "3h", "45m", "0m".
 */
export function formatDarkCore(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}m`;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}

export interface DarkCoreInput {
  galacticCoreVisible: boolean;
  darkCoreMinutes?: number;
}

/** "Dark core time 3h 20m", or null when the core isn't visible in the dark. */
export function darkCoreLabel(day: DarkCoreInput): string | null {
  return day.galacticCoreVisible && has(day.darkCoreMinutes) ? `Dark core time ${formatDarkCore(day.darkCoreMinutes)}` : null;
}

/** Excel/CSV column in decimal hours, so it sorts and sums; blank when the core isn't visible. */
export function darkCoreColumns(day: DarkCoreInput): Record<string, number | string> {
  return {
    'Dark Core Hours': day.galacticCoreVisible && has(day.darkCoreMinutes) ? Math.round(day.darkCoreMinutes / 6) / 10 : '',
  };
}
